// src/admin/AdminContacts.jsx
import React, { useEffect, useState } from 'react';
import axios from 'axios';

function AdminContacts() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all'); // all | unread | read

  // Réponse en cours
  const [replyId, setReplyId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replySubject, setReplySubject] = useState('');
  const [sending, setSending] = useState(false);

  // Suppression
  const [deletingId, setDeletingId] = useState(null);

  /* =========================================================
     CHARGEMENT
  ========================================================= */
  const fetchMessages = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('auth_token');
      const { data } = await axios.get('https://madadline.com/api/contacts', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setError('Impossible de charger les messages.');
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMessages(); }, []);

  /* =========================================================
     MARQUER COMME LU
  ========================================================= */
  const markAsRead = async (id) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.put(
        `https://madadline.com/api/contacts/${id}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(prev =>
        prev.map(m => m.id === id ? { ...m, read: true, read_at: new Date().toISOString() } : m)
      );
    } catch (e) {
      console.error(e);
    }
  };

  /* =========================================================
     RÉPONDRE
  ========================================================= */
  const startReply = (m) => {
    setReplyId(m.id);
    setReplyText('');
    setReplySubject('Réponse à votre message');
    // Marquer automatiquement comme lu
    if (!m.read) markAsRead(m.id);
  };

  const cancelReply = () => {
    setReplyId(null);
    setReplyText('');
    setReplySubject('');
  };

  const sendReply = async (id) => {
    if (!replyText.trim()) {
      alert('Le message ne peut pas être vide.');
      return;
    }
    setSending(true);
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(
        `https://madadline.com/api/contacts/${id}/reply`,
        { message: replyText.trim(), subject: replySubject.trim() || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(prev =>
        prev.map(m => m.id === id
          ? { ...m, admin_reply: replyText.trim(), replied_at: new Date().toISOString(), read: true }
          : m
        )
      );
      cancelReply();
      alert('Réponse envoyée avec succès.');
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.error || 'Erreur lors de l\'envoi.');
    } finally {
      setSending(false);
    }
  };

  /* =========================================================
     SUPPRIMER
  ========================================================= */
  const deleteMessage = async (id) => {
    if (!window.confirm('Supprimer définitivement ce message ?')) return;
    setDeletingId(id);
    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(`https://madadline.com/api/contacts/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages(prev => prev.filter(m => m.id !== id));
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.error || 'Erreur lors de la suppression.');
    } finally {
      setDeletingId(null);
    }
  };

  /* =========================================================
     FILTRE + COMPTEURS
  ========================================================= */
  const filtered = messages.filter(m => {
    if (filter === 'unread') return !m.read;
    if (filter === 'read') return m.read;
    return true;
  });

  const unreadCount = messages.filter(m => !m.read).length;

  /* =========================================================
     RENDER
  ========================================================= */
  return (
    <div className="admin-contacts">

      <header className="admin-page-head">
        <div>
          <h1>Messages de contact</h1>
          <p>
            {messages.length} message{messages.length > 1 ? 's' : ''}
            {unreadCount > 0 && (
              <span style={{
                marginLeft: 10,
                background: '#C21E56',
                color: 'white',
                padding: '2px 8px',
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 600,
              }}>
                {unreadCount} non lu{unreadCount > 1 ? 's' : ''}
              </span>
            )}
          </p>
        </div>
        <button
          className="admin-btn admin-btn--ghost"
          onClick={fetchMessages}
          disabled={loading}
        >
          ↻ Rafraîchir
        </button>
      </header>

      {/* Filtres */}
      <div className="admin-status-filters" style={{ marginBottom: 20 }}>
        <button
          className={`admin-chip ${filter === 'all' ? 'is-active' : ''}`}
          onClick={() => setFilter('all')}
        >
          Tous ({messages.length})
        </button>
        <button
          className={`admin-chip ${filter === 'unread' ? 'is-active' : ''}`}
          onClick={() => setFilter('unread')}
        >
          Non lus ({unreadCount})
        </button>
        <button
          className={`admin-chip ${filter === 'read' ? 'is-active' : ''}`}
          onClick={() => setFilter('read')}
        >
          Lus ({messages.length - unreadCount})
        </button>
      </div>

      {loading ? (
        <div className="admin-loading"><div className="spinner" /></div>
      ) : error ? (
        <p className="admin-empty" style={{ color: '#c00' }}>{error}</p>
      ) : filtered.length === 0 ? (
        <p className="admin-empty">
          {filter === 'unread'
            ? 'Aucun message non lu.'
            : filter === 'read'
              ? 'Aucun message lu.'
              : 'Aucun message reçu.'}
        </p>
      ) : (
        <div className="admin-messages">
          {filtered.map(m => {
            const isReplying = replyId === m.id;
            const isDeleting = deletingId === m.id;

            return (
              <div
                key={m.id}
                className="admin-message"
                style={{
                  opacity: isDeleting ? 0.4 : 1,
                  borderLeft: !m.read ? '4px solid #C21E56' : '4px solid transparent',
                }}
              >
                {/* En-tête */}
                <div className="admin-message__head">
                  <strong>{m.name || 'Anonyme'}</strong>
                  <span>{m.email || m.phone || ''}</span>
                  {!m.read && (
                    <span className="admin-badge admin-badge--warning" style={{ fontSize: 10 }}>
                      Non lu
                    </span>
                  )}
                  {m.replied_at && (
                    <span className="admin-badge admin-badge--success" style={{ fontSize: 10 }}>
                      ✓ Répondu
                    </span>
                  )}
                  <span className="admin-message__date">
                    {new Date(m.created_at).toLocaleString('fr-FR')}
                  </span>
                </div>

                {/* Message original */}
                <p>{m.message}</p>

                {/* Réponse déjà envoyée */}
                {m.admin_reply && !isReplying && (
                  <div style={{
                    marginTop: 14,
                    padding: 14,
                    background: '#F0FDF4',
                    border: '1px solid #86EFAC',
                    borderRadius: 8,
                    fontSize: 14,
                  }}>
                    <strong style={{ color: '#16A34A' }}>Votre réponse</strong>
                    <span style={{ marginLeft: 8, fontSize: 11, color: '#888' }}>
                      {m.replied_at ? new Date(m.replied_at).toLocaleString('fr-FR') : ''}
                    </span>
                    <p style={{ marginTop: 6, marginBottom: 0 }}>{m.admin_reply}</p>
                  </div>
                )}

                {/* Formulaire de réponse */}
                {isReplying && (
                  <div style={{ marginTop: 14 }}>
                    <input
                      type="text"
                      placeholder="Sujet (optionnel)"
                      value={replySubject}
                      onChange={e => setReplySubject(e.target.value)}
                      className="admin-input"
                      style={{ width: '100%', marginBottom: 8 }}
                    />
                    <textarea
                      placeholder="Votre réponse..."
                      value={replyText}
                      onChange={e => setReplyText(e.target.value)}
                      rows={5}
                      className="admin-input"
                      style={{ width: '100%', resize: 'vertical', marginBottom: 10 }}
                      maxLength={2000}
                    />
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button
                        className="admin-btn admin-btn--primary"
                        onClick={() => sendReply(m.id)}
                        disabled={sending}
                      >
                        {sending ? 'Envoi…' : '📤 Envoyer la réponse'}
                      </button>
                      <button
                        className="admin-btn admin-btn--ghost"
                        onClick={cancelReply}
                        disabled={sending}
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                )}

                {/* Actions */}
                {!isReplying && (
                  <div style={{ marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {!m.read && (
                      <button
                        className="admin-btn admin-btn--ghost"
                        onClick={() => markAsRead(m.id)}
                        disabled={isDeleting}
                      >
                        👁 Marquer lu
                      </button>
                    )}
                    <button
                      className="admin-btn admin-btn--primary"
                      onClick={() => startReply(m)}
                      disabled={isDeleting}
                    >
                      ✉️ Répondre
                    </button>
                    <button
                      className="admin-btn admin-btn--danger"
                      onClick={() => deleteMessage(m.id)}
                      disabled={isDeleting}
                    >
                      {isDeleting ? '…' : '🗑 Supprimer'}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default AdminContacts;